<section class="search-shell" role="search" aria-label="Search flights">

  <!-- ======================= Desktop / tablet ======================= -->
  <div class="search-surface search-surface--desktop">
    <form class="search-body" novalidate autocomplete="off" [formGroup]="form" (submit)="onSubmit($event)">

      <div class="search-toolbar">
        <div class="trip-toggle" role="radiogroup" aria-label="Trip type">
          <button
            type="button"
            class="trip-toggle__btn"
            role="radio"
            [attr.aria-checked]="returning"
            [class.trip-toggle__btn--active]="returning"
            (click)="setTripType(true)">
            <i class="fas fa-exchange-alt" aria-hidden="true"></i>
            Round trip
          </button>
          <button
            type="button"
            class="trip-toggle__btn"
            role="radio"
            [attr.aria-checked]="!returning"
            [class.trip-toggle__btn--active]="!returning"
            (click)="setTripType(false)">
            <i class="fas fa-long-arrow-alt-right" aria-hidden="true"></i>
            One way
          </button>
        </div>
      </div>

      <div class="search-bar" [class.search-bar--invalid]="showErrors">

        <!-- From -->
        <div class="field field--from" matAutocompleteOrigin #fromOrigin="matAutocompleteOrigin" [class.field--error]="submitAttempted && (errors.from || errors.sameAirport)">
          <span class="field__icon" aria-hidden="true"><i class="fas fa-plane-departure"></i></span>
          <div class="field__content">
            <span class="field__label">From</span>
            <app-from
              class="field__control airport-control"
              formControlName="pickup"
              [placeholder]="fromPlaceholder"
              [panelOrigin]="fromOrigin">
            </app-from>
          </div>
        </div>

        <button
          type="button"
          class="swap-btn"
          (click)="swap()"
          [disabled]="!fromData && !destinationData"
          aria-label="Swap departure and destination"
          title="Swap">
          <i class="fas fa-exchange-alt" aria-hidden="true"></i>
        </button>

        <!-- To -->
        <div class="field field--to" matAutocompleteOrigin #toOrigin="matAutocompleteOrigin" [class.field--error]="submitAttempted && (errors.to || errors.sameAirport)">
          <span class="field__icon" aria-hidden="true"><i class="fas fa-plane-arrival"></i></span>
          <div class="field__content">
            <span class="field__label">To</span>
            <app-dropdown
              class="field__control airport-control"
              formControlName="dropoff"
              [placeholder]="toPlaceholder"
              [panelOrigin]="toOrigin">
            </app-dropdown>
          </div>
        </div>

        <!-- Departure -->
        <div
          class="field field--date field--depart"
          [class.field--error]="submitAttempted && errors.depart"
          (click)="departPicker.open()">
          <span class="field__icon" aria-hidden="true"><i class="far fa-calendar-alt"></i></span>
          <div class="field__content">
            <span class="field__label">Departure</span>
            <div class="date-control">
              <span class="date-control__value" [class.date-control__value--placeholder]="!departDate">
                {{ departDate ? formatDay(departDate) : 'Add date' }}
              </span>
              <mat-form-field class="date-picker-host" appearance="fill" subscriptSizing="dynamic">
                <input
                  matInput
                  readonly
                  tabindex="-1"
                  aria-label="Departure date"
                  formControlName="traveldate"
                  [min]="minDate"
                  [matDatepicker]="departPicker"
                  (dateChange)="onDepartChange()" />
                <mat-datepicker #departPicker></mat-datepicker>
              </mat-form-field>
            </div>
          </div>
        </div>

        <!-- Return -->
        <div
          class="field field--date field--return"
          [class.field--ghost]="!returning"
          [class.field--error]="submitAttempted && (errors.return || errors.dateOrder)"
          (click)="onReturnFieldClick(returnPicker)">
          <span class="field__icon" aria-hidden="true"><i class="far fa-calendar-alt"></i></span>
          <div class="field__content">
            <span class="field__label">Return</span>
            <div class="date-control">
              <span class="date-control__value" [class.date-control__value--placeholder]="!returning || !returnDate">
                {{ returning ? (returnDate ? formatDay(returnDate) : 'Add date') : '+ Add return' }}
              </span>
              <mat-form-field class="date-picker-host" appearance="fill" subscriptSizing="dynamic">
                <input
                  matInput
                  readonly
                  tabindex="-1"
                  aria-label="Return date"
                  formControlName="returndate"
                  [min]="returnMinDate"
                  [matDatepicker]="returnPicker"
                  (dateChange)="onReturnChange()" />
                <mat-datepicker #returnPicker></mat-datepicker>
              </mat-form-field>
            </div>
          </div>
        </div>

        <!-- Travellers & class -->
        <div class="field field--pax">
          <span class="field__icon" aria-hidden="true"><i class="fas fa-user-friends"></i></span>
          <div class="field__content">
            <span class="field__label">Travellers &amp; class</span>
            <app-passengers class="field__control pax-control" [initial]="passengers"></app-passengers>
          </div>
        </div>

        <!-- CTA -->
        <div class="field field--cta">
          <button type="submit" class="search-btn" [class.search-btn--wiggle]="shaking" aria-describedby="flight-search-error">
            <i class="fas fa-search" aria-hidden="true"></i>
            <span>Search flights</span>
          </button>
        </div>
      </div>

      @if (showErrors) {
        <p id="flight-search-error" class="form-alert" role="alert">
          <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
          {{ validationHint }}
        </p>
      }

      @if (variant === 'hero') {
        <div class="search-meta">
          <div class="popular-row" aria-label="Popular routes from Nairobi">
            <span class="popular-row__label">
              <i class="fas fa-bolt" aria-hidden="true"></i>
              Popular from Nairobi:
            </span>
            @for (route of popularRoutes; track trackRoute($index, route)) {
              <button
                type="button"
                class="popular-chip"
                [class.popular-chip--busy]="busyRoute === trackRoute($index, route)"
                [disabled]="busyRoute !== null"
                (click)="searchPopular(route)">
                {{ route.label }}
              </button>
            }
          </div>

          <div class="search-footer" aria-label="Payment methods">
            <span class="search-footer__note">
              <i class="fas fa-lock" aria-hidden="true"></i>
              Pay securely with
            </span>
            <div class="payment-logos">
              <img height="20" alt="M-Pesa" src="https://buupassstatic.blob.core.windows.net/marketplace/assets/brands/mpesa.webp" />
              <img height="20" alt="Airtel Money" src="https://buupassstatic.blob.core.windows.net/marketplace/assets/brands/airtel.webp" />
              <img height="20" alt="Visa" src="https://buupassstatic.blob.core.windows.net/marketplace/assets/brands/paystack-visa.png" />
            </div>
          </div>
        </div>
      }
    </form>
  </div>

  <!-- ============================ Mobile ============================ -->
  <form class="mobile-search" novalidate (submit)="onSubmit($event)">
    <div class="mobile-search__header">
      <div class="trip-toggle" role="radiogroup" aria-label="Trip type">
        <button
          type="button"
          class="trip-toggle__btn"
          role="radio"
          [attr.aria-checked]="returning"
          [class.trip-toggle__btn--active]="returning"
          (click)="setTripType(true)">
          <i class="fas fa-exchange-alt" aria-hidden="true"></i>
          Round trip
        </button>
        <button
          type="button"
          class="trip-toggle__btn"
          role="radio"
          [attr.aria-checked]="!returning"
          [class.trip-toggle__btn--active]="!returning"
          (click)="setTripType(false)">
          <i class="fas fa-long-arrow-alt-right" aria-hidden="true"></i>
          One way
        </button>
      </div>
    </div>

    <fieldset class="mobile-search__fieldset">
      <legend class="mobile-search__legend">Flight</legend>

      <div class="mobile-route">
        <button
          type="button"
          class="mobile-field"
          [class.mobile-field--error]="submitAttempted && (errors.from || errors.sameAirport)"
          (click)="openFromSheet()">
          <span class="mobile-field__icon" aria-hidden="true"><i class="fas fa-plane-departure"></i></span>
          <span class="mobile-field__text">
            <span class="mobile-field__label">From</span>
            <span class="mobile-field__value" [class.mobile-field__value--placeholder]="!fromData">
              {{ fromData ? fromLabel : 'Leaving from' }}
            </span>
          </span>
          <i class="fas fa-chevron-right mobile-field__chevron" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="mobile-swap"
          (click)="swap()"
          [disabled]="!fromData && !destinationData"
          aria-label="Swap departure and destination">
          <i class="fas fa-exchange-alt" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="mobile-field"
          [class.mobile-field--error]="submitAttempted && (errors.to || errors.sameAirport)"
          (click)="openToSheet()">
          <span class="mobile-field__icon" aria-hidden="true"><i class="fas fa-plane-arrival"></i></span>
          <span class="mobile-field__text">
            <span class="mobile-field__label">To</span>
            <span class="mobile-field__value" [class.mobile-field__value--placeholder]="!destinationData">
              {{ destinationData ? toLabel : 'Going to' }}
            </span>
          </span>
          <i class="fas fa-chevron-right mobile-field__chevron" aria-hidden="true"></i>
        </button>
      </div>

      <div class="mobile-dates">
        <button
          type="button"
          class="mobile-field"
          [class.mobile-field--error]="submitAttempted && errors.depart"
          (click)="openDepartSheet()">
          <span class="mobile-field__icon" aria-hidden="true"><i class="far fa-calendar-alt"></i></span>
          <span class="mobile-field__text">
            <span class="mobile-field__label">Departure</span>
            <span class="mobile-field__value" [class.mobile-field__value--placeholder]="!departDate">
              {{ departDate ? formatDay(departDate) : 'Add date' }}
            </span>
          </span>
          <i class="fas fa-chevron-right mobile-field__chevron" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="mobile-field"
          [class.mobile-field--ghost]="!returning"
          [class.mobile-field--error]="submitAttempted && (errors.return || errors.dateOrder)"
          (click)="openReturnSheet()">
          <span class="mobile-field__icon" aria-hidden="true"><i class="far fa-calendar-alt"></i></span>
          <span class="mobile-field__text">
            <span class="mobile-field__label">Return</span>
            <span class="mobile-field__value" [class.mobile-field__value--placeholder]="!returning || !returnDate">
              {{ returning ? (returnDate ? formatDay(returnDate) : 'Add date') : '+ Add return' }}
            </span>
          </span>
          <i class="fas fa-chevron-right mobile-field__chevron" aria-hidden="true"></i>
        </button>
      </div>

      <button type="button" class="mobile-field" (click)="openPassengersSheet()">
        <span class="mobile-field__icon" aria-hidden="true"><i class="fas fa-user-friends"></i></span>
        <span class="mobile-field__text">
          <span class="mobile-field__label">Travellers &amp; class</span>
          <span class="mobile-field__value">{{ passengerSummary }}</span>
        </span>
        <i class="fas fa-chevron-right mobile-field__chevron" aria-hidden="true"></i>
      </button>
    </fieldset>

    @if (showErrors) {
      <p id="flight-search-error-mobile" class="form-alert" role="alert">
        <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
        {{ validationHint }}
      </p>
    }

    <button type="submit" class="search-btn search-btn--block" [class.search-btn--wiggle]="shaking" aria-describedby="flight-search-error-mobile">
      <i class="fas fa-search" aria-hidden="true"></i>
      <span>Search flights</span>
    </button>

    @if (variant === 'hero') {
      <div class="popular-row popular-row--mobile" aria-label="Popular routes from Nairobi">
        <span class="popular-row__label">
          <i class="fas fa-bolt" aria-hidden="true"></i>
          Popular:
        </span>
        @for (route of popularRoutes; track trackRoute($index, route)) {
          <button
            type="button"
            class="popular-chip"
            [class.popular-chip--busy]="busyRoute === trackRoute($index, route)"
            [disabled]="busyRoute !== null"
            (click)="searchPopular(route)">
            {{ route.label }}
          </button>
        }
      </div>

      <footer class="search-footer search-footer--mobile">
        <span class="search-footer__note">
          <i class="fas fa-lock" aria-hidden="true"></i>
          Pay securely with
        </span>
        <div class="payment-logos" aria-label="Payment methods">
          <img height="18" alt="M-Pesa" src="https://buupassstatic.blob.core.windows.net/marketplace/assets/brands/mpesa.webp" />
          <img height="18" alt="Airtel Money" src="https://buupassstatic.blob.core.windows.net/marketplace/assets/brands/airtel.webp" />
          <img height="18" alt="Visa" src="https://buupassstatic.blob.core.windows.net/marketplace/assets/brands/paystack-visa.png" />
        </div>
      </footer>
    }
  </form>
</section>
